import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { integrationCompaniesApi, integrationCategoriesApi, uploadApi, STORAGE_BASE_URL } from '../../services/adminApi';

const IntegrationCompanyForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({
    category_id: '',
    name: '',
    logo: '',
    description: '',
    sort_order: 0,
    is_active: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchCategories();
    if (isEdit) {
      fetchCompany();
    }
  }, [id]);

  const fetchCategories = async () => {
    try {
      const response = await integrationCategoriesApi.getAll();
      if (response.data.success) {
        // Check if data is array or object with data property
        const categoriesData = Array.isArray(response.data.data) 
          ? response.data.data 
          : response.data.data?.data || [];
        setCategories(categoriesData);
      } else {
        setCategories([]);
      }
    } catch (err) {
      console.error('Categories fetch error:', err);
      setCategories([]);
    }
  };

  const fetchCompany = async () => {
    try {
      setLoading(true);
      const response = await integrationCompaniesApi.getById(id);
      if (response.data.success) {
        const company = response.data.data;
        setFormData({
          category_id: company.category_id || '',
          name: company.name || '',
          logo: company.logo ? `${STORAGE_BASE_URL}${company.logo}` : '',
          description: company.description || '',
          sort_order: company.sort_order || 0,
          is_active: company.is_active !== undefined ? company.is_active : true
        });
      } else {
        setError('Firma bulunamadı');
      }
    } catch (err) {
      console.error('Company fetch error:', err);
      setError('Firma yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    const allowedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];
    const fileExtension = file.name.split('.').pop().toLowerCase();

    if (!allowedTypes.includes(file.type) || !allowedExtensions.includes(fileExtension)) {
      alert('Sadece JPG, PNG, GIF, WEBP, SVG dosyaları yüklenebilir.');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Dosya boyutu çok büyük. Maksimum 5MB olmalıdır.');
      return;
    }

    setSelectedFile(file);
    setUploading(true);

    try {
      const response = await uploadApi.upload(file);
      if (response.data.success) {
        // Backend'den dönen URL'yi frontend'de kullanmak için base URL ekle
        const logoUrl = `${STORAGE_BASE_URL}${response.data.data.url}`;
        setFormData(prev => ({
          ...prev,
          logo: logoUrl
        }));
      } else {
        alert('Dosya yüklenirken hata oluştu: ' + response.data.message);
      }
    } catch (err) {
      console.error('Upload error:', err);
      alert('Dosya yüklenirken hata oluştu: ' + (err.response?.data?.message || err.message));
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.category_id) {
      setError('Kategori seçimi gereklidir');
      return;
    }
    if (!formData.name.trim()) {
      setError('Firma adı gereklidir');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Prepare data for submission
      const submitData = { ...formData };
      
      // Convert logo URL back to relative path for backend
      if (submitData.logo && submitData.logo.startsWith(STORAGE_BASE_URL)) {
        submitData.logo = submitData.logo.replace(STORAGE_BASE_URL, '');
      }

      let response;
      if (isEdit) {
        response = await integrationCompaniesApi.update(id, submitData);
      } else {
        response = await integrationCompaniesApi.create(submitData);
      }

      if (response.data.success) {
        alert(isEdit ? 'Firma başarıyla güncellendi!' : 'Firma başarıyla eklendi!');
        navigate('/admin/integration-companies');
      } else {
        setError(response.data.message || 'Bir hata oluştu');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const containerStyle = {
    padding: '1.5rem',
    backgroundColor: '#f9fafb',
    minHeight: '100vh'
  };

  const formContainerStyle = {
    backgroundColor: 'white',
    borderRadius: '0.5rem',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
    padding: '2rem',
    maxWidth: '48rem',
    margin: '0 auto'
  };

  const titleStyle = {
    fontSize: '1.875rem',
    fontWeight: '700',
    color: '#111827',
    marginBottom: '2rem',
    textAlign: 'center'
  };

  const formGroupStyle = {
    marginBottom: '1.5rem'
  };

  const labelStyle = {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '0.5rem'
  };

  const inputStyle = {
    width: '100%',
    padding: '0.75rem 1rem',
    fontSize: '1rem',
    lineHeight: '1.5',
    color: '#111827',
    backgroundColor: '#ffffff',
    border: '1px solid #d1d5db',
    borderRadius: '0.375rem',
    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
    boxSizing: 'border-box'
  };

  const textareaStyle = {
    ...inputStyle,
    minHeight: '6rem',
    resize: 'vertical'
  };

  const fileInputStyle = {
    ...inputStyle,
    padding: '0.5rem'
  };

  const logoPreviewStyle = {
    width: '8rem',
    height: '8rem',
    borderRadius: '0.5rem',
    objectFit: 'cover',
    border: '1px solid #e5e7eb',
    backgroundColor: '#f9fafb',
    marginTop: '0.5rem'
  };

  const checkboxContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '2rem'
  };

  const checkboxStyle = {
    marginRight: '0.75rem',
    width: '1.25rem',
    height: '1.25rem',
    cursor: 'pointer'
  };

  const checkboxLabelStyle = {
    ...labelStyle,
    marginBottom: 0,
    cursor: 'pointer'
  };

  const buttonContainerStyle = {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '1rem',
    marginTop: '2rem'
  };

  const buttonStyle = {
    padding: '0.75rem 1.5rem',
    fontSize: '1rem',
    fontWeight: '500',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    transition: 'all 0.15s ease-in-out',
    border: 'none',
    minWidth: '7rem'
  };

  const cancelButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#f3f4f6',
    color: '#374151',
    border: '1px solid #d1d5db'
  };

  const submitButtonStyle = {
    ...buttonStyle,
    backgroundColor: loading ? '#9ca3af' : '#3b82f6',
    color: 'white',
    border: 'none'
  };

  const errorStyle = {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#dc2626',
    padding: '0.75rem',
    borderRadius: '0.375rem',
    marginBottom: '1.5rem'
  };

  const loadingStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '16rem'
  };

  const spinnerStyle = {
    width: '2rem',
    height: '2rem',
    border: '2px solid #e5e7eb',
    borderTop: '2px solid #3b82f6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  };

  if (loading && !isEdit) {
    return (
      <div style={containerStyle}>
        <div style={loadingStyle}>
          <div style={spinnerStyle}></div>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{isEdit ? 'Entegrasyon Firması Düzenle' : 'Yeni Entegrasyon Firması Ekle'} - Admin Panel</title>
      </Helmet>
      <div style={containerStyle}>
        <div style={formContainerStyle}>
          <h1 style={titleStyle}>
            {isEdit ? 'Entegrasyon Firması Düzenle' : 'Yeni Entegrasyon Firması Ekle'}
          </h1>

          {error && (
            <div style={errorStyle}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={formGroupStyle}>
              <label htmlFor="category_id" style={labelStyle}>
                Kategori Seçimi *
              </label>
              <select
                id="category_id"
                name="category_id"
                value={formData.category_id}
                onChange={handleChange}
                style={inputStyle}
                required
              >
                <option value="">Kategori Seçiniz</option>
                {categories && categories.length > 0 ? categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.title}
                  </option>
                )) : (
                  <option value="" disabled>Kategoriler yükleniyor...</option>
                )}
              </select>
            </div>

            <div style={formGroupStyle}>
              <label htmlFor="name" style={labelStyle}>
                Firma Adı *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                style={inputStyle}
                placeholder="Örn: PayPal"
                required
              />
            </div>

            <div style={formGroupStyle}>
              <label htmlFor="logo" style={labelStyle}>
                Logo
              </label>
              <input
                type="file"
                id="logo"
                name="logo"
                onChange={handleFileChange}
                style={fileInputStyle}
                accept="image/*"
                disabled={uploading}
              />
              {formData.logo && (
                <img 
                  src={formData.logo} 
                  alt="Logo Preview" 
                  style={logoPreviewStyle}
                />
              )}
              {uploading && (
                <div style={{ marginTop: '0.5rem', color: '#6b7280', fontSize: '0.875rem' }}>
                  Logo yükleniyor...
                </div>
              )}
            </div>

            <div style={formGroupStyle}>
              <label htmlFor="description" style={labelStyle}>
                Açıklama
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                style={textareaStyle}
                placeholder="Firma hakkında detaylı açıklama..."
              />
            </div>

            <div style={formGroupStyle}>
              <label htmlFor="sort_order" style={labelStyle}>
                Sıralama
              </label>
              <input
                type="number"
                id="sort_order"
                name="sort_order"
                value={formData.sort_order}
                onChange={handleChange}
                style={inputStyle}
                placeholder="0"
                min="0"
              />
            </div>

            <div style={checkboxContainerStyle}>
              <input
                type="checkbox"
                id="is_active"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                style={checkboxStyle}
              />
              <label htmlFor="is_active" style={checkboxLabelStyle}>
                Aktif
              </label>
            </div>

            <div style={buttonContainerStyle}>
              <button
                type="button"
                onClick={() => navigate('/admin/integration-companies')}
                style={cancelButtonStyle}
                disabled={loading}
              >
                İptal
              </button>
              <button
                type="submit"
                style={submitButtonStyle}
                disabled={loading || uploading}
              >
                {loading ? 'İşleniyor...' : (isEdit ? 'Güncelle' : 'Ekle')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default IntegrationCompanyForm;
